import React, { useState, useRef, useCallback, useEffect, ReactElement, MutableRefObject } from 'react';

import inputStyles from './AlmeInput.styles';

import { Button, IconButton, TextField, InputAdornment } from '@material-ui/core';
import { withStyles, WithStyles, makeStyles, createStyles } from '@material-ui/core/styles';

import { FocusComponentProps } from '../../models/ComponentProps';

import { AlmeApiUtils } from '../../api/AlmeApiUtils';

import { EntryOriginTypes } from '@alme/api-ts/Alme/V6/EntryOriginTypes';
import { AlmeInputConfig } from './AlmeInputConfig';
import { useIntl } from 'react-intl';
import { useFocusStyles } from '../FocusVisibleStyle';

const useStyles = makeStyles(() =>
    createStyles({
        root: {
            display: 'flex',
            alignItems: 'center',
            margin: 0,
            padding: 0,
        },
        customInputLabel: {
            zIndex: 'unset',
        },
    }),
);

interface AlmeInputProps extends WithStyles<typeof inputStyles> {
    inputId: string;
    submitId: string;
    submitFunc: (input: string) => void;
    almeConfig: AlmeInputConfig;
    isLiveChatConnected?: boolean;
    onSendTypingEvent?: () => void;
    focusProps?: FocusComponentProps;
}

const AlmeInput: React.FC<AlmeInputProps> = (props: AlmeInputProps) => {
    const intl = useIntl();
    const classes = useStyles();
    const Focusclasses = useFocusStyles();
    const [userInput, setInput] = useState<string>('');
    const [hasError, setError] = useState<boolean>(false);
    const [isTyping, setIsTyping] = useState<boolean>(false);

    const userTypingEventDelay = props.almeConfig.input.userTypingEventDelay || 10000;
    useEffect(() => {
        let timer: NodeJS.Timeout | null = null;
        if (isTyping) {
            timer = setTimeout(() => {
                setIsTyping(false);
            }, userTypingEventDelay);
        }

        return () => {
            if (timer !== null) {
                clearTimeout(timer);
            }
        };
    }, [isTyping, userTypingEventDelay]);

    const useAlmeInputRef = useRef<HTMLInputElement>(null);

    useEffect(() => {
        useAlmeInputRef?.current?.focus();
    }, []);

    /**
     * Handles input box change using the setInput state function.
     * This function handles max input length and will not allow
     * text longer than props.maxInputLength to be added.
     */
    const textChanged = (event: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>): void => {
        let inputText = event.target.value;

        if (inputText?.length) {
            setError(false);
        }

        if (inputText.length > props.almeConfig.input.maxInputLength) {
            inputText = inputText.substring(0, props.almeConfig.input.maxInputLength);
        }

        if (props.isLiveChatConnected && typeof props.onSendTypingEvent === 'function' && !isTyping) {
            setIsTyping(true);
            props.onSendTypingEvent();
        }

        setInput(inputText);
    };

    /**
     * Submits the inputText using supplied submitFunc function
     * if the text is not empty or whitespace and then clears
     * the input box using the setInput state function.
     */
    const onSubmitInput = (): void => {
        const inputText = userInput.trim();

        if (inputText) {
            props.submitFunc(inputText);
            setInput('');
            // ? TODO: should we reset typing when the input is submitted?
            //setIsTyping(false);
        } else {
            setError(true);
            useAlmeInputRef?.current?.focus();
        }
    };

    const onKeyDown = (event: React.KeyboardEvent<HTMLInputElement>): void => {
        // 'keypress' event misbehaves on mobile so we track 'Enter' key via 'keydown' event
        if (event.key === 'Enter') {
            event.preventDefault();
            event.stopPropagation();
            onSubmitInput();
        }
    };

    const getHelperText = (almeConfig: AlmeInputConfig): string => {
        if (hasError && almeConfig.input.displayError) {
            return intl.formatMessage({
                id: "input.inputErrorMessage",
                defaultMessage: 'Default input error message',
                description: 'AlmeInput error helper text',
            });
        }
        return `${userInput.length}/${props.almeConfig.input.maxInputLength}`;
    };

    const accessibilityProps= getAccessibilityProps();

    const sharedInputProps = {
        id: props.inputId,
        error: props.almeConfig.input.displayError && hasError,
        className: props.classes.almeInputBox,
        value: userInput,
        label:
            intl.formatMessage({
                id: "input.inputPlaceHolderText",
                defaultMessage: 'Default input placeholderText',
                description: 'Alme Input placeholder text',
            }),
        'aria-label':
            intl.formatMessage({
                id: "input.inputTextAriaLabel",
                defaultMessage: 'Default input text aria label',
                description: 'Alme Input text aria label',
            }),
        inputProps: { className: props.classes.textField },
        margin: 'normal' as const,
        onChange: textChanged,
        onKeyDown,
        fullWidth: true,
        inputRef: useAlmeInputRef,
        helperText: getHelperText(props.almeConfig),
        FormHelperTextProps: accessibilityProps,
        variant: props.almeConfig.input.styles.textField.variant,
        color: props.almeConfig.input.styles.textField.color,
        size: props.almeConfig.input.styles.textField.size,
        InputLabelProps: { className: `${props.classes.inputLabel}` },
        autoFocus: props.almeConfig.input.shouldAutoFocus,
    };

    const sharedButtonProps = {
        id: props.submitId,
        onClick: onSubmitInput,
        'aria-label': intl.formatMessage({
            id: "input.sendButtonAriaLabel",
            defaultMessage: 'Default input send button aria label',
            description: 'AlmeInput send button aria label',
        }),
        color: props.almeConfig.input.styles.button.color,
    };

    const sharedFormProps = {
        className: classes.root,
        noValidate: true,
        autoComplete: "off",
    };

    const sharedTextInputProps = {
    };

    const getAlmeInput = (): ReactElement => {
        if (props.almeConfig.input.submitButtonText) {
            return (
                <>
                    <TextField
                        {...sharedInputProps}
                        {...props.focusProps}
                        InputProps={{
                            ...sharedTextInputProps,
                        }}
                        InputLabelProps={{classes: {outlined: classes.customInputLabel}}}
                    />
                    <Button
                        {...sharedButtonProps}
                        {...props.focusProps}
                        className={`${props.classes.submitButton} ${Focusclasses.visibleFocus}`}
                        size='medium'
                        variant='contained'
                        endIcon={props.almeConfig.input.sendIcon}
                    >{intl.formatMessage({
                        id: "input.sendButtonText",
                        defaultMessage: 'Default send button text',
                        description: 'Send button text if present',
                    })}</Button >
                </>
            );
        } else {
            return (
                <TextField
                    {...sharedInputProps}
                    {...props.focusProps}
                    InputProps={{
                        ...sharedTextInputProps,
                        endAdornment: (
                            <InputAdornment position="end">
                                <IconButton
                                    className={`${props.classes.submitIcon} ${Focusclasses.visibleFocus} `}
                                    {...sharedButtonProps}
                                    {...props.focusProps}
                                >{props.almeConfig.input.sendIcon}</IconButton>
                            </InputAdornment>),
                    }}
                    InputLabelProps={{classes: {outlined: classes.customInputLabel}}}
                />
            );
        }
    };

    return (
        <form {...sharedFormProps} className={classes.root}>
            {getAlmeInput()}
        </form>
    );

    function getAccessibilityProps() {
        return hasError ? {
            className: `${props.classes.textField}`,
            role: 'alert',
            'aria-atomic': true,
        } : {className: `${props.classes.textField}`};
    }
};

export default withStyles(inputStyles)(AlmeInput);

export function OnSubmitAlmeInput(apiUtils: React.MutableRefObject<AlmeApiUtils>, scrollToResponse: MutableRefObject<boolean>): (input: string, origin?: EntryOriginTypes, displayText?: string, parameters?: Record<string, unknown>) => void {
    return useCallback(async (input: string, origin?: EntryOriginTypes, displayText?: string, parameters?: Record<string, unknown>) => {
        scrollToResponse.current = true;
        await apiUtils.current.submitAlmeInput(input, origin, displayText, parameters);
    }, [apiUtils, scrollToResponse]);
}

export function OnSendTypingEvent(apiUtils: React.MutableRefObject<AlmeApiUtils>): () => Promise<void> {
    return useCallback(async () => {
        await apiUtils.current.submitReportingEvent('Typing');
    }, [apiUtils]);
}
